/**
 * Footer section of the single-order layout.
 *
 * Wraps the design-system {@link Footer} with the Start-Filing / Submit /
 * Go-Back / Review buttons so {@link SingleOrderLayout} stays declarative.
 *
 * @module SingleOrderFooter
 */
import React from "react";

import { Button, Footer } from "@risalabs_frontend_org/oasis-ui-kit";
import ReviewButton from "./reviewButton";

interface SingleOrderFooterProps {
  startFilingButtonText: string;
  submitButtonText: string;
  shouldShowStartFilingButton: boolean;
  shouldShowReviewButton: boolean;
  hasReviewDocuments: boolean;
  isSubmitDisabled: boolean;
  /**
   * Tooltip shown on the disabled submit button. When omitted, a generic
   * "complete all required fields" message is used as a fallback.
   */
  tooltipContent?: React.ReactNode;
  orderId: string;
  onStartFilingClick: () => void;
  onSubmitClick: () => void;
  onBackClick: () => void;
}

const SingleOrderFooter: React.FC<SingleOrderFooterProps> = ({
  startFilingButtonText,
  submitButtonText,
  shouldShowStartFilingButton,
  shouldShowReviewButton,
  hasReviewDocuments,
  isSubmitDisabled,
  tooltipContent,
  orderId,
  onStartFilingClick,
  onSubmitClick,
  onBackClick,
}) => {
  return (
    <Footer
      cancelButtonText={startFilingButtonText}
      submitButtonText={submitButtonText}
      isDisabled={isSubmitDisabled}
      onCancelClick={
        shouldShowStartFilingButton ? onStartFilingClick : onBackClick
      }
      onSubmitClick={onSubmitClick}
      useNative={false}
      showTooltip={isSubmitDisabled ? true : false}
      tooltipContent={
        isSubmitDisabled
          ? (tooltipContent ??
            "Please complete all required fields before submitting.")
          : ""
      }

      children={
        <>
          {shouldShowStartFilingButton && (
            <Button
              buttonType={"tertiary"}
              size={"medium"}
              onClick={onBackClick}
              disabled={false}
              children={"Go Back"}
            />
          )}
          {shouldShowReviewButton && (
            <ReviewButton
              orderId={orderId}
              hasReviewDocuments={hasReviewDocuments}
            />
          )}
        </>
      }
    />
  );
};

export default SingleOrderFooter;
